/** Admin Members — API mappers & listing config. */

import { enrichRequestRow } from "../Requests/adminRequestsData"
import {
  formatDisplayName,
  getUserFullName,
} from "../../../helpers/userDisplay"

export const MEMBER_STATUS_TABS = [
  { key: "all", label: "All" },
  { key: "active", label: "Active" },
  { key: "inactive", label: "Inactive" },
]

/** City Members tabs — counts from GET /api/v1/admin/members/users-summary */
export const CITY_MEMBER_STATUS_TABS = MEMBER_STATUS_TABS.map(tab => ({
  ...tab,
}))

/**
 * Map members summary → ListingTabs items with live counts.
 * Summary: { all, active, inactive }
 */
export const mapMemberStatusTabs = (result = {}, baseTabs = MEMBER_STATUS_TABS) => {
  const total = Number(result.all ?? result.total_members ?? result.total)
  const active = Number(result.active ?? result.approved)
  const inactive = Number(result.inactive)

  const countByKey = {
    all: Number.isFinite(total) ? total : undefined,
    active: Number.isFinite(active) ? active : undefined,
    inactive: Number.isFinite(inactive) ? inactive : undefined,
  }

  return baseTabs.map(tab => {
    const count = countByKey[tab.key]
    if (typeof count !== "number") return { ...tab }
    return { ...tab, count }
  })
}

export const MEMBER_SORT_OPTIONS = [
  { value: "first_name", label: "First Name" },
  { value: "last_name", label: "Last Name" },
  { value: "email", label: "Email" },
  { value: "type", label: "Type" },
  { value: "created_at", label: "Created At" },
  { value: "updated_at", label: "Updated At" },
  { value: "total_xp", label: "Total XP" },
]

export const DEFAULT_MEMBER_SORT_BY = "created_at"
export const DEFAULT_MEMBER_SORT_DIRECTION = "desc"

/** GET /api/v1/admin/members list eager-load. */
export const MEMBER_LIST_WITH = ["addresses.city"]

/** GET /api/v1/admin/invites/requests list eager-load (Pending / Rejected). */
export const MEMBER_INVITE_LIST_WITH = ["addresses.cities"]

/** Join Platform–aligned status filter options for Members listing. */
export const MEMBER_INVITE_STATUS_FILTER_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Dismissed" },
  { value: "withdrawn", label: "Withdrawn" },
]

const formatInviteCity = city => {
  if (!city) return "--"
  if (typeof city === "string") return city.trim() || "--"
  const name = city.city || city.name || ""
  const country = city.country || city.state || ""
  if (name && country) return `${name}, ${country}`
  return name || country || "--"
}

const resolveInviteCityLabel = item => {
  if (item.city) return formatInviteCity(item.city)

  const addresses = item.addresses
  if (Array.isArray(addresses)) {
    for (let index = 0; index < addresses.length; index += 1) {
      const address = addresses[index]
      const nestedCities = address?.cities
      if (Array.isArray(nestedCities) && nestedCities.length > 0) {
        return formatInviteCity(nestedCities[0])
      }
      if (nestedCities && !Array.isArray(nestedCities)) {
        return formatInviteCity(nestedCities)
      }
      if (address?.city) return formatInviteCity(address.city)
    }
  }

  return "--"
}

/** Normalize invite / request status from API. */
export const normalizeInviteRequestStatus = raw => {
  const value = String(raw ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")

  if (
    !value ||
    value === "pending" ||
    value === "open" ||
    value === "requested" ||
    value === "waiting" ||
    value === "new"
  ) {
    return "pending"
  }
  if (
    value === "approved" ||
    value === "accepted" ||
    value === "resolved" ||
    value === "approve" ||
    value === "active"
  ) {
    return "approved"
  }
  if (
    value === "rejected" ||
    value === "dismissed" ||
    value === "denied" ||
    value === "reject" ||
    value === "dismiss"
  ) {
    return "rejected"
  }
  if (value === "withdrawn") {
    return "withdrawn"
  }
  return value
}

export const canDecideInviteRequest = status =>
  normalizeInviteRequestStatus(status) === "pending"

/** Map member tab / UI status → invite requests API status. */
export const toInviteApiStatus = uiStatus => {
  if (uiStatus === "active") return "approved"
  if (uiStatus === "all") return ""
  return uiStatus
}

/** Map invite requests API status → member UI status. */
export const toMemberUiStatus = apiStatus => {
  const normalized = normalizeInviteRequestStatus(apiStatus)
  if (normalized === "approved") return "active"
  if (normalized === "withdrawn") return "rejected"
  return normalized
}

const formatInviteDate = value => {
  if (!value) return "--"
  try {
    const parsed = new Date(value)
    if (Number.isNaN(parsed.getTime())) return "--"
    return parsed.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    })
  } catch {
    return "--"
  }
}

/** ULID embeds ms timestamp in the first 10 Crockford Base32 chars. */
const resolveDateFromUlid = id => {
  const raw = String(id || "")
    .trim()
    .toUpperCase()
  if (raw.length < 10) return ""
  const alphabet = "0123456789ABCDEFGHJKMNPQRSTVWXYZ"
  let time = 0
  for (let i = 0; i < 10; i += 1) {
    const idx = alphabet.indexOf(raw[i])
    if (idx < 0) return ""
    time = time * 32 + idx
  }
  const date = new Date(time)
  return Number.isNaN(date.getTime()) ? "" : date.toISOString()
}

const resolveMemberStatus = item => {
  const statusRaw = String(item?.status || "")
    .trim()
    .toLowerCase()
  if (statusRaw === "inactive" || statusRaw === "active") return statusRaw
  if (
    item.is_active === 0 ||
    item.is_active === false ||
    item.is_active === "0"
  ) {
    return "inactive"
  }
  return "active"
}

const resolveMemberCreatedAt = item => {
  if (item.created_at) return item.created_at
  if (item.user?.created_at) return item.user.created_at

  const projectMembers = item.project_members || item.projectMembers || []
  const dates = (Array.isArray(projectMembers) ? projectMembers : [])
    .map(member => member?.created_at || member?.joined_at)
    .filter(Boolean)
    .map(value => new Date(value).getTime())
    .filter(time => Number.isFinite(time))
    .sort((a, b) => a - b)

  if (dates.length > 0) return new Date(dates[0]).toISOString()

  // List payload often omits created_at — ULID still encodes create time.
  return resolveDateFromUlid(item.id) || item.updated_at || ""
}

/** Resolve platform user id from an approved invite request row. */
export const resolveInviteMemberUserId = item =>
  item?.user_id ||
  item?.user?.id ||
  item?.member_id ||
  item?.member?.id ||
  item?.profile_id ||
  ""

/**
 * Map GET /api/v1/admin/invites/requests item → Members table row.
 * Includes `request` payload for Join Platform detail / decision modals.
 */
export const mapInviteRequestToMemberRow = item => {
  const firstName = formatDisplayName(item.first_name || "")
  const lastName = formatDisplayName(item.last_name || "")
  const name =
    getUserFullName({
      first_name: firstName,
      last_name: lastName,
      email: item.email,
    }) ||
    item.email ||
    "Unknown"
  const requestStatus = normalizeInviteRequestStatus(
    item.status ?? item.state ?? item.request_status,
  )
  const status = toMemberUiStatus(requestStatus)

  const request = enrichRequestRow({
    id: item.id,
    requestType: "join_platform",
    name,
    firstName: firstName || "--",
    lastName: lastName || "--",
    email: item.email || "",
    avatar:
      item.avatar ||
      item.profile_photo ||
      item.profile_media?.thumbnail ||
      item.profile_media?.image ||
      "",
    city: resolveInviteCityLabel(item),
    cityId: item.city_id || item.city?.id || "",
    projectName: "--",
    reason: item.reason || "--",
    date: formatInviteDate(item.created_at),
    status: requestStatus,
    currentLimit: "--",
    requestedLimit: "--",
    decisionReason: item.decision_reason || "",
    handlerName: getUserFullName(item.handler),
    handledAt: item.handled_at ? formatInviteDate(item.handled_at) : "",
    raw: item,
  })

  return {
    id: item.id,
    userId: resolveInviteMemberUserId(item),
    name,
    firstName,
    lastName,
    email: item.email || "--",
    city: resolveInviteCityLabel(item),
    status,
    requestStatus,
    isActive: status === "active",
    joinedDate: formatInviteDate(item.created_at || item.approved_at),
    image: request.avatar,
    request,
    raw: item,
  }
}

const formatCount = value => {
  const num = Number(value)
  if (!Number.isFinite(num)) return "--"
  return num.toLocaleString("en-US")
}

/** Map GET /api/v1/admin/members/users-summary → KPI cards. */
export const mapMemberKpi = (result = {}) => [
  {
    key: "total",
    title: "Total Members",
    value: formatCount(result.all ?? result.total_members ?? result.total),
    tone: "lightblue-custom",
  },
  {
    key: "active",
    title: "Active",
    value: formatCount(result.active ?? result.approved),
    tone: "lightteal",
  },
  {
    key: "inactive",
    title: "Inactive",
    value: formatCount(result.inactive),
    tone: "purple-custom",
  },
]

const formatCityLabel = city => {
  if (!city) return ""
  if (typeof city === "string") return city.trim()
  const name = city.city || city.name || ""
  const state = city.state || city.country || ""
  if (name && state) return `${name}, ${state}`
  return name
}

const resolveMemberCityLabel = item => {
  const addresses = Array.isArray(item.addresses) ? item.addresses : []
  const typeOrder = { primary: 0, secondary: 1, tertiary: 2 }
  const labels = [...addresses]
    .sort((a, b) => {
      const aType = String(a?.address_type || "").toLowerCase()
      const bType = String(b?.address_type || "").toLowerCase()
      return (typeOrder[aType] ?? 99) - (typeOrder[bType] ?? 99)
    })
    .map(address => formatCityLabel(address?.city) || formatCityLabel(address))
    .filter(Boolean)

  if (labels.length > 0) {
    return [...new Set(labels)].join(", ")
  }

  return (
    formatCityLabel(item.city) ||
    formatCityLabel(item.preferred_city) ||
    formatCityLabel(item.address?.city) ||
    ""
  )
}

/**
 * Map GET /api/v1/admin/members item → table row.
 */
export const mapMemberRow = item => {
  const firstName = formatDisplayName(item.first_name || "")
  const lastName = formatDisplayName(item.last_name || "")
  const name =
    getUserFullName({
      ...item,
      first_name: firstName || item.first_name,
      last_name: lastName || item.last_name,
    }) || "--"
  const city = resolveMemberCityLabel(item)
  const status = resolveMemberStatus(item)
  const createdAt = formatInviteDate(resolveMemberCreatedAt(item))

  return {
    id: item.id,
    name,
    firstName,
    lastName,
    email: item.email || "--",
    city: city || "--",
    status,
    isActive: status === "active",
    type: item.type || "",
    totalXp: Number(item.total_xp) || 0,
    totalProjects: Number(item.total_projects) || 0,
    createdAt,
    joinedDate: createdAt,
    image:
      item.profile_media?.thumbnail ||
      item.profile_media?.image ||
      item.thumbnail ||
      item.image ||
      "",
    raw: item,
  }
}

/** Format city object from GET /api/v1/admin/members/:email cities.* */
export const formatAdminMemberCity = city => {
  if (!city) return "--"
  if (typeof city === "string") return city.trim() || "--"
  const name = city.city || city.name || ""
  const state = city.state || ""
  if (name && state) return `${name}, ${state}`
  return name || "--"
}

/** City label for editable inputs — empty string instead of "--". */
export const formatAdminMemberCityInput = city => {
  const formatted = formatAdminMemberCity(city)
  return formatted === "--" ? "" : formatted
}

const resolveAdminMemberPhoneNumber = (result = {}, profile = {}) =>
  profile.phone_number ||
  profile.phone ||
  result.phone_number ||
  result.phone ||
  result.user?.phone_number ||
  result.user?.phone ||
  ""

/**
 * Map GET /api/v1/admin/members/:email result → detail view model.
 */
export const mapAdminMemberDetail = (result = {}) => {
  const profile = result.profile || {}
  const cities = result.cities || {}
  const projects = result.projects || {}
  const artGenome = result.art_genome || {}

  return {
    id: profile.id || "",
    email: profile.email || "",
    first_name: profile.first_name || "",
    last_name: profile.last_name || "",
    full_name: profile.full_name || "",
    bio: profile.bio || "",
    phone_number: resolveAdminMemberPhoneNumber(result, profile),
    role: profile.role || null,
    total_xp: profile.total_xp,
    listening_to: profile.listening_to,
    reading: profile.reading,
    profile_media: profile.profile_media || {},
    image:
      profile.profile_media?.thumbnail || profile.profile_media?.image || "",
    cities: {
      primary: cities.primary || null,
      secondary: cities.secondary || null,
      tertiary: cities.tertiary || null,
    },
    projects: {
      total: Number(projects.total) || 0,
      active: Number(projects.active) || 0,
      past: Number(projects.past) || 0,
      future: Number(projects.future) || 0,
    },
    art_genome: {
      spellbook: artGenome.spellbook || {},
      class: artGenome.class || {},
    },
    ongoing_roles: Array.isArray(result.ongoing_roles)
      ? result.ongoing_roles
      : [],
    raw: result,
  }
}

const resolveAdminMemberCityId = (cityObj, profile = {}, type = "") => {
  if (cityObj?.id) return String(cityObj.id)
  const addresses = Array.isArray(profile.addresses) ? profile.addresses : []
  const address = addresses.find(entry => entry?.address_type === type)
  return String(address?.city_id || address?.city?.id || "")
}

/** Map GET /api/v1/admin/members/:id → AdminMemberEdit Formik values (member portal fields). */
export const mapAdminMemberEditFormValues = (result = {}) => {
  const mapped = mapAdminMemberDetail(result)
  const profile = result.profile || {}

  return {
    firstName: mapped.first_name || "",
    lastName: mapped.last_name || "",
    email: mapped.email || "",
    phone_number: mapped.phone_number || "",
    listeningTo: mapped.listening_to || "",
    reading: mapped.reading || "",
    primaryCityId: resolveAdminMemberCityId(
      mapped.cities?.primary,
      profile,
      "primary",
    ),
    secondaryCityId: resolveAdminMemberCityId(
      mapped.cities?.secondary,
      profile,
      "secondary",
    ),
    tertiaryCityId: resolveAdminMemberCityId(
      mapped.cities?.tertiary,
      profile,
      "tertiary",
    ),
  }
}

export const mapAdminMemberEditLockedCities = (result = {}) => {
  const mapped = mapAdminMemberDetail(result)
  const values = mapAdminMemberEditFormValues(result)

  return {
    locked: {
      primary: Boolean(values.primaryCityId),
      secondary: Boolean(values.secondaryCityId),
      tertiary: Boolean(values.tertiaryCityId),
    },
    labels: {
      primary: values.primaryCityId
        ? formatAdminMemberCityInput(mapped.cities?.primary)
        : "",
      secondary: values.secondaryCityId
        ? formatAdminMemberCityInput(mapped.cities?.secondary)
        : "",
      tertiary: values.tertiaryCityId
        ? formatAdminMemberCityInput(mapped.cities?.tertiary)
        : "",
    },
  }
}

/** Static KPI fallback until list/summary load (also used by City Portal). */
export const MEMBER_KPI = mapMemberKpi()

/**
 * Static fixtures for City Portal Members until its API is wired.
 * Admin Members listing uses live GET /api/v1/admin/members instead.
 */
export const MEMBER_ROWS = [
  {
    id: "1",
    name: "Alara Voss",
    email: "alara@amok.earth",
    city: "Cape Town, SA",
    status: "active",
    joinedDate: "March 15, 2024",
  },
  {
    id: "2",
    name: "Hiroshi Tanaka",
    email: "hiroshi@amok.earth",
    city: "Toronto, CA",
    status: "active",
    joinedDate: "January 8, 2024",
  },
  {
    id: "3",
    name: "Elena Petrova",
    email: "elena@amok.earth",
    city: "Moscow, RU",
    status: "pending",
    joinedDate: "April 22, 2024",
  },
  {
    id: "4",
    name: "Mateo Ruiz",
    email: "mateo@amok.earth",
    city: "Mexico City, MX",
    status: "active",
    joinedDate: "June 1, 2024",
  },
  {
    id: "5",
    name: "Clara Byrne",
    email: "clara@amok.earth",
    city: "Dublin, IE",
    status: "rejected",
    joinedDate: "October 10, 2024",
  },
  {
    id: "6",
    name: "Noah Kim",
    email: "noah@amok.earth",
    city: "Seoul, KR",
    status: "active",
    joinedDate: "February 20, 2024",
  },
  {
    id: "7",
    name: "Sofia Rossi",
    email: "sofia@amok.earth",
    city: "Milan, IT",
    status: "pending",
    joinedDate: "May 3, 2024",
  },
  {
    id: "8",
    name: "James Okonkwo",
    email: "james@amok.earth",
    city: "Lagos, NG",
    status: "active",
    joinedDate: "July 17, 2024",
  },
  {
    id: "9",
    name: "Amira Hassan",
    email: "amira@amok.earth",
    city: "Cairo, EG",
    status: "active",
    joinedDate: "August 4, 2024",
  },
  {
    id: "10",
    name: "Lucas Berg",
    email: "lucas@amok.earth",
    city: "Stockholm, SE",
    status: "rejected",
    joinedDate: "September 12, 2024",
  },
  {
    id: "11",
    name: "Priya Mehta",
    email: "priya@amok.earth",
    city: "Mumbai, IN",
    status: "active",
    joinedDate: "November 1, 2024",
  },
  {
    id: "12",
    name: "Daniel Costa",
    email: "daniel@amok.earth",
    city: "Lisbon, PT",
    status: "pending",
    joinedDate: "December 9, 2024",
  },
  {
    id: "13",
    name: "Yuki Nakamura",
    email: "yuki@amok.earth",
    city: "Tokyo, JP",
    status: "active",
    joinedDate: "January 21, 2025",
  },
  {
    id: "14",
    name: "Olivia Chen",
    email: "olivia@amok.earth",
    city: "Singapore, SG",
    status: "active",
    joinedDate: "February 14, 2025",
  },
  {
    id: "15",
    name: "Marcus Webb",
    email: "marcus@amok.earth",
    city: "Austin, US",
    status: "pending",
    joinedDate: "March 2, 2025",
  },
]
